import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  FileText,
  Layers,
  Loader2,
  Lock,
  LogOut,
  Monitor,
  MoreVertical,
  Package,
  Plus,
  Printer,
  RotateCcw,
  Wrench,
} from 'lucide-react';
import PakRupeeIcon from '@/components/common/PakRupeeIcon';
import { useShiftBarActionsStore } from '@/store/shiftBarActionsStore';

export interface PosMobileOverflowMenuProps {
  canProcessReturns: boolean;
  userCanAddExpenses: boolean;
  showTodaySales: boolean;
  showCustomerDisplay: boolean;
  sellOnlyProducts: boolean;
  viewMode: 'products' | 'services';
  pendingCount: number;
  heldCount?: number;
  onReturn: () => void;
  onExpense: () => void;
  onPending: () => void;
  onTodaySales: () => void;
  onCustomerDisplay: () => void;
  onViewModeChange: (mode: 'products' | 'services') => void;
  onHeldCarts?: () => void;
}

export default function PosMobileOverflowMenu({
  canProcessReturns,
  userCanAddExpenses,
  showTodaySales,
  showCustomerDisplay,
  sellOnlyProducts,
  viewMode,
  pendingCount,
  heldCount = 0,
  onReturn,
  onExpense,
  onPending,
  onTodaySales,
  onCustomerDisplay,
  onViewModeChange,
  onHeldCarts,
}: PosMobileOverflowMenuProps) {
  const { t } = useTranslation();
  const shiftActions = useShiftBarActionsStore((s) => s.actions);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const run = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <div className="relative shrink-0" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
        aria-label={t('common.more')}
      >
        <MoreVertical className="w-5 h-5" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 rounded-xl bg-white border border-gray-100 shadow-xl z-[100] py-1 animate-fade-in">
          {!sellOnlyProducts && (
            <>
              <button
                type="button"
                onClick={() => run(() => onViewModeChange('products'))}
                className={`w-full text-left px-4 py-3 text-sm font-medium flex items-center gap-2.5 min-h-[44px] ${
                  viewMode === 'products' ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Package className="w-4 h-4 shrink-0" />
                {t('pos.products')}
              </button>
              <button
                type="button"
                onClick={() => run(() => onViewModeChange('services'))}
                className={`w-full text-left px-4 py-3 text-sm font-medium flex items-center gap-2.5 min-h-[44px] ${
                  viewMode === 'services' ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Wrench className="w-4 h-4 shrink-0" />
                {t('pos.services')}
              </button>
              <div className="border-t border-gray-100 my-1" />
            </>
          )}
          {heldCount > 0 && onHeldCarts && (
            <button
              type="button"
              onClick={() => run(onHeldCarts)}
              className="w-full text-left px-4 py-3 text-sm font-medium text-amber-900 hover:bg-amber-50 flex items-center gap-2.5 min-h-[44px]"
            >
              <Layers className="w-4 h-4 shrink-0" />
              <span className="flex-1">{t('pos.heldCarts')}</span>
              <span className="bg-amber-600 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
                {heldCount}
              </span>
            </button>
          )}
          {canProcessReturns && (
            <button
              type="button"
              onClick={() => run(onReturn)}
              className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 min-h-[44px]"
            >
              <RotateCcw className="w-4 h-4 shrink-0" />
              {t('pos.return')}
            </button>
          )}
          {userCanAddExpenses && (
            <button
              type="button"
              onClick={() => run(onExpense)}
              className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 min-h-[44px]"
            >
              <PakRupeeIcon className="w-4 h-4 shrink-0" />
              {t('pos.expense')}
            </button>
          )}
          <button
            type="button"
            onClick={() => run(onPending)}
            className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 min-h-[44px]"
          >
            <Clock className="w-4 h-4 shrink-0" />
            <span className="flex-1">{t('pos.pending')}</span>
            {pendingCount > 0 && (
              <span className="bg-amber-600 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
          </button>
          {showTodaySales && (
            <button
              type="button"
              onClick={() => run(onTodaySales)}
              className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 min-h-[44px]"
            >
              <FileText className="w-4 h-4 shrink-0" />
              {t('pos.todaySales')}
            </button>
          )}
          {showCustomerDisplay && (
            <button
              type="button"
              onClick={() => run(onCustomerDisplay)}
              className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 min-h-[44px]"
            >
              <Monitor className="w-4 h-4 shrink-0" />
              {t('pos.customerDisplay')}
            </button>
          )}
          {shiftActions?.shiftOpen && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <button
                type="button"
                onClick={() => run(shiftActions.openPaidIn)}
                className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 min-h-[44px]"
              >
                <Plus className="w-4 h-4 shrink-0 text-emerald-700" />
                {t('shift.paidIn')}
              </button>
              <button
                type="button"
                onClick={() => run(shiftActions.openPaidOut)}
                className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 min-h-[44px]"
              >
                <PakRupeeIcon className="w-4 h-4 shrink-0 text-orange-700" />
                {t('shift.paidOut')}
              </button>
              {shiftActions.userCanAddExpenses && (
                <>
                  <button
                    type="button"
                    onClick={() => run(shiftActions.openDrawerExpense)}
                    className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 min-h-[44px]"
                  >
                    <Plus className="w-4 h-4 shrink-0" />
                    {t('shift.addDrawerExpense')}
                  </button>
                  <button
                    type="button"
                    onClick={() => run(shiftActions.openTodayExpenses)}
                    className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 min-h-[44px]"
                  >
                    <PakRupeeIcon className="w-4 h-4 shrink-0" />
                    {t('shift.todaysExpenses')}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => run(shiftActions.lockScreen)}
                className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 min-h-[44px]"
              >
                <Lock className="w-4 h-4 shrink-0" />
                {t('shift.lockScreen')}
              </button>
              <button
                type="button"
                onClick={() => run(shiftActions.printX)}
                disabled={shiftActions.printingX}
                className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 min-h-[44px] disabled:opacity-60"
              >
                {shiftActions.printingX ? (
                  <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                ) : (
                  <Printer className="w-4 h-4 shrink-0" />
                )}
                {t('xReport.printX')}
              </button>
              <button
                type="button"
                onClick={() => run(shiftActions.endShift)}
                className="w-full text-left px-4 py-3 text-sm font-medium text-red-700 hover:bg-red-50 flex items-center gap-2.5 min-h-[44px]"
              >
                <LogOut className="w-4 h-4 shrink-0" />
                {t('shift.endShift')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
